// Converts a friendly "every day / every week / every month at HH:MM" choice
// into a 6-field cron expression (sec min hour day-of-month month day-of-week),
// matching the "0 0 9 * * *" style example from your swagger doc.
//
// NOTE: day-of-week is emitted as numeric 0-6 (0 = Sunday). Some cron parsers
// (Quartz, Spring's CronExpression) accept MON-SUN names instead, and Quartz
// also requires "?" instead of "*" in one of day-of-month/day-of-week. Check
// what your backend's cron parser expects and adjust WEEKDAYS/format below
// if numeric doesn't work.

export const WEEKDAYS = [
  { value: 0, label: "Sun" },
  { value: 1, label: "Mon" },
  { value: 2, label: "Tue" },
  { value: 3, label: "Wed" },
  { value: 4, label: "Thu" },
  { value: 5, label: "Fri" },
  { value: 6, label: "Sat" },
];

export const FREQUENCY_OPTIONS = [
  { value: "daily", label: "Every day" },
  { value: "weekly", label: "Every week" },
  { value: "monthly", label: "Every month" },
  { value: "custom", label: "Custom (raw cron expression)" },
];

const pad = (n) => String(n).padStart(2, "0");

export const buildCronExpression = ({
  frequency,
  time,
  daysOfWeek,
  dayOfMonth,
}) => {
  const [hour = "0", minute = "0"] = (time || "09:00").split(":");
  const h = Number(hour);
  const m = Number(minute);

  switch (frequency) {
    case "daily":
      return `0 ${m} ${h} * * *`;
    case "weekly": {
      const days = daysOfWeek?.length ? [...daysOfWeek].sort().join(",") : "*";
      return `0 ${m} ${h} * * ${days}`;
    }
    case "monthly":
      return `0 ${m} ${h} ${dayOfMonth || 1} * *`;
    default:
      return ""; // custom: caller keeps whatever raw text was typed
  }
};

// Best-effort reverse parse, used when editing an existing record so the
// friendly builder can be pre-filled instead of always falling back to
// "custom". Returns null if the expression doesn't match a pattern this
// builder produces (e.g. hand-written complex cron), in which case the form
// should fall back to "custom" mode with the raw string.
export const parseCronExpression = (expr) => {
  if (!expr || typeof expr !== "string") return null;
  const parts = expr.trim().split(/\s+/);
  if (parts.length !== 6) return null;

  const [sec, min, hour, dayOfMonth, month, dayOfWeek] = parts;
  if (sec !== "0" || month !== "*") return null;
  if (Number.isNaN(Number(min)) || Number.isNaN(Number(hour))) return null;

  const time = `${pad(Number(hour))}:${pad(Number(min))}`;

  if (dayOfMonth === "*" && dayOfWeek === "*") {
    return { frequency: "daily", time, daysOfWeek: [], dayOfMonth: 1 };
  }
  if (dayOfMonth === "*" && dayOfWeek !== "*") {
    const days = dayOfWeek
      .split(",")
      .map(Number)
      .filter((n) => !Number.isNaN(n));
    if (!days.length) return null;
    return { frequency: "weekly", time, daysOfWeek: days, dayOfMonth: 1 };
  }
  if (dayOfMonth !== "*" && dayOfWeek === "*") {
    const dom = Number(dayOfMonth);
    if (Number.isNaN(dom)) return null;
    return { frequency: "monthly", time, daysOfWeek: [], dayOfMonth: dom };
  }
  return null;
};

const MONTH_NAMES = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const capitalize = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

// Expands a single cron field ("*", "8", "1,3,5", "1-5", "*/15", "1-20/5")
// into a sorted array of numbers, or null if the field is "*" ("every").
const expandCronField = (field) => {
  if (field === "*") return null;
  const values = new Set();

  field.split(",").forEach((token) => {
    const stepMatch = token.match(/^(\*|\d+-\d+|\d+)\/(\d+)$/);
    if (stepMatch) {
      const [, range, stepStr] = stepMatch;
      const step = Number(stepStr) || 1;
      if (range === "*") {
        // Step over an unknown-bounded "*" — caller only ever needs the
        // rendered phrase, so a representative small run is enough context
        // (e.g. "*/15" -> 0, 15, 30, 45 relies on min/hour fields elsewhere).
        for (let v = 0; v <= 59; v += step) values.add(v);
      } else {
        const [a, b] = range.split("-").map(Number);
        for (let v = a; v <= (b ?? a); v += step) values.add(v);
      }
      return;
    }
    const rangeMatch = token.match(/^(\d+)-(\d+)$/);
    if (rangeMatch) {
      const a = Number(rangeMatch[1]);
      const b = Number(rangeMatch[2]);
      for (let v = a; v <= b; v++) values.add(v);
      return;
    }
    if (/^\d+$/.test(token)) values.add(Number(token));
  });

  const list = [...values].sort((a, b) => a - b);
  return list.length ? list : null;
};

const weekdayLabel = (d) => WEEKDAYS.find((w) => w.value === d % 7)?.label || d;

// Turns any 6-field cron expression (sec min hour day-of-month month
// day-of-week) into a plain-English sentence — it interprets the fields
// directly instead of only recognizing the exact shapes the friendly
// builder above produces, so it never falls back to showing raw cron syntax
// like "* 0 8 * * *". Always returns a readable string.
export const describeCronExpression = (expr) => {
  if (!expr) return "—";
  const parts = String(expr).trim().split(/\s+/);
  if (parts.length !== 6) return expr; // not a recognizable cron shape at all

  const [secField, minField, hourField, domField, monthField, dowField] = parts;

  const secVals = expandCronField(secField);
  const minVals = expandCronField(minField);
  const hourVals = expandCronField(hourField);
  const domVals = expandCronField(domField);
  const monthVals = expandCronField(monthField);
  const dowVals = expandCronField(dowField);

  // Day-of-week / day-of-month phrase (standard cron OR-semantics when both
  // are restricted).
  let dayPhrase = "every day";
  if (domVals && dowVals) {
    dayPhrase = `on day ${domVals.join(", ")} of the month, or on ${dowVals
      .map(weekdayLabel)
      .join(", ")}`;
  } else if (domVals) {
    dayPhrase = `on day ${domVals.join(", ")} of the month`;
  } else if (dowVals) {
    dayPhrase = `every ${dowVals.map(weekdayLabel).join(", ")}`;
  }

  const monthPhrase = monthVals
    ? ` in ${monthVals.map((m) => MONTH_NAMES[m - 1] || m).join(", ")}`
    : "";

  // Clean "at HH:MM" phrasing when there's a single unambiguous hour and
  // minute (covers the vast majority of real schedules, including a
  // wildcard/"*" seconds field, which doesn't affect the displayed time).
  if (hourVals?.length === 1 && minVals?.length === 1) {
    const time = `${pad(hourVals[0])}:${pad(minVals[0])}`;
    return `${capitalize(dayPhrase)} at ${time}${monthPhrase}`;
  }

  // Multiple/step-based hours or minutes (e.g. "every 15 minutes") — still
  // describe it in words rather than showing the raw expression.
  const hourPhrase = !hourVals
    ? "every hour"
    : hourVals.length === 1
      ? `${pad(hourVals[0])}:00`
      : `hours ${hourVals.map(pad).join(", ")}`;
  const minPhrase = !minVals
    ? "every minute"
    : minVals.length === 1
      ? `minute ${pad(minVals[0])}`
      : `minutes ${minVals.map(pad).join(", ")}`;
  const secPhrase =
    secVals && !(secVals.length === 1 && secVals[0] === 0)
      ? secVals.length === 1
        ? `, second ${secVals[0]}`
        : `, seconds ${secVals.join(", ")}`
      : "";

  return `${capitalize(dayPhrase)}, at ${hourPhrase}, ${minPhrase}${secPhrase}${monthPhrase}`;
};
